import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayerDrawerToken } from 'components/token/PlayerDrawerToken';

import { ManagerSalesHeader } from '../ManagerSalesHeader';
import { PlayerDrawerTokenSkeleton } from '../ui';
import type { PlayerMarketDrawerManagerSales_anyCard } from './__generated__/index.graphql';
import { useManagerSalesTag } from './useManagerSalesTag';

const Root = styled(Vertical).attrs({
  gap: 0,
})`
  flex-grow: 1;
  border-left: 1px solid var(--c-nd-150-opaque);
`;

const Sales = styled(Vertical)`
  position: relative;
  isolation: isolate;
  width: 100%;
  padding: 0 var(--double-unit) var(--double-unit);
  @media ${laptopAndAbove} {
    padding: 0 var(--triple-unit) var(--triple-unit);
  }
`;

type Props = {
  anyCards: PlayerMarketDrawerManagerSales_anyCard[];
  slugs: string[];
  count: number;
};

export const ManagerSales = ({ anyCards, slugs, count }: Props) => {
  const { bestPrice, highestLevel } = useManagerSalesTag({ anyCards });
  return (
    <Root>
      <ManagerSalesHeader count={count} />
      <Sales>
        {slugs.map(slug => {
          const anyCard = anyCards.find(c => slug === c.slug);
          if (!anyCard) return <PlayerDrawerTokenSkeleton key={slug} />;
          return (
            <PlayerDrawerToken
              key={slug}
              anyCard={anyCard}
              bestPrice={bestPrice === anyCard.slug}
              highestLevel={highestLevel === anyCard.slug}
            />
          );
        })}
      </Sales>
    </Root>
  );
};

ManagerSales.fragments = {
  anyCard: gql`
    fragment PlayerMarketDrawerManagerSales_anyCard on AnyCardInterface {
      slug
      ...useManagerSalesTag_anyCard
      ...PlayerDrawerToken_anyCard
    }
    ${useManagerSalesTag.fragments.anyCard}
    ${PlayerDrawerToken.fragments.anyCard}
  ` as TypedDocumentNode<PlayerMarketDrawerManagerSales_anyCard>,
};
